import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Container,
  Paper,
  Fade,
  Grow,
  Card,
  CardContent,
  Button,
  IconButton,
  Popover,
  CircularProgress,
  Alert,
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  CalendarMonth,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker } from 'react-date-range';
import { format, addDays, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
  ReferenceLine,
} from 'recharts';
import { getStoredToken, authenticatedFetch } from '../utils/api';

const GREEN_COLOR = '#10b981'; // Positive values (enrollments)
const RED_COLOR = '#ef4444';   // Negative values (deletions)

const IntrusionProfileEnrollment = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  // Date range state (default to last 7 days)
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const datePickerOpen = Boolean(datePickerAnchor);

  useEffect(() => {
    // Check if user is authenticated
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Fetch company name from banner settings (optional)
    const fetchCompanyName = async () => {
      try {
        const response = await authenticatedFetch(`/settings/company`);
        const data = await response.json();
        if (data.company_name) {
          setCompanyName(data.company_name);
        }
      } catch (err) {
        console.log('Company name not available, using default');
        setCompanyName('Intrusion Profile Enrollment');
      }
    };
    
    fetchCompanyName();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      const startDate = format(dateRange[0].startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange[0].endDate, 'yyyy-MM-dd');
      
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      params.append('is_intrusion', 'true'); // Filter for intrusion profiles only
      
      const response = await authenticatedFetch(`/dashboard/analytics?${params}`);
      const data = await response.json();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message || 'Unable to load enrollment data');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!analyticsData || !analyticsData.daily_counts || analyticsData.daily_counts.length === 0) {
      return [];
    }

    const dailyCounts = analyticsData.daily_counts || [];
    
    // Fill in missing dates in the range
    const startDate = new Date(dateRange[0].startDate);
    const endDate = new Date(dateRange[0].endDate);
    const allDates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const existingData = dailyCounts.find(d => d.date === dateStr);
      allDates.push({
        date: dateStr,
        displayDate: format(currentDate, 'MMM dd'),
        count: existingData ? existingData.count : 0,
        enrollments: existingData ? existingData.enrollments : 0,
        deletions: existingData ? existingData.deletions : 0,
        index: allDates.length,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  };

  const chartData = prepareChartData();
  const netRegistrations = analyticsData?.total_registrations || 0;
  const dateRangeLabel = analyticsData?.date_range 
    ? `${analyticsData.date_range.start_date} to ${analyticsData.date_range.end_date}`
    : '';

  // Calculate Y-axis domain
  const allValues = chartData.map(d => d.count);
  const maxValue = Math.max(...allValues, 0);
  const minValue = Math.min(...allValues, 0);
  const yAxisMax = Math.max(Math.abs(maxValue), Math.abs(minValue), 3);
  const yAxisDomain = [-yAxisMax, yAxisMax];

  const renderIntrusionEnrollment = () => (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Container 
        maxWidth="xl" 
        sx={{ 
          pt: 0,
          pb: 0,
          px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {/* Hero Header Section */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              background: '#dc2626',
              borderRadius: { xs: 2, sm: 3, md: 4 },
              p: { xs: 1.5, sm: 2, md: 3 },
              mb: { xs: 2, sm: 2, md: 1 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(220, 38, 38, 0.3)',
              width: '100%',
            }}
          >
            {/* Decorative Background Elements */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: -30, sm: -40, md: -50 },
                right: { xs: -30, sm: -40, md: -50 },
                width: { xs: 120, sm: 160, md: 200 },
                height: { xs: 120, sm: 160, md: 200 },
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                filter: 'blur(40px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: -20, sm: -25, md: -30 },
                left: { xs: -20, sm: -25, md: -30 },
                width: { xs: 100, sm: 120, md: 150 },
                height: { xs: 100, sm: 120, md: 150 },
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
                filter: 'blur(30px)',
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 0, 
                  flexWrap: 'wrap', 
                  gap: { xs: 1.5, sm: 2 },
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: { xs: 2, sm: 3 },
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0,
                  }}
                >
                  <TrendingUpIcon sx={{ color: 'white', fontSize: { xs: 32, sm: 36, md: 40 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                  <Typography 
                    variant="h3" 
                    component="h1"
                    sx={{ 
                      color: 'white',
                      fontWeight: 500,
                      mb: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '1rem', sm: '1.35rem', md: '1.5rem', lg: '1.5rem' },
                      textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                      lineHeight: 1.2,
                    }}
                  >
                    Intrusion Profile Enrollment
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                      fontWeight: 400,
                      mt: { xs: 0.5, sm: 0 },
                    }}
                  >
                    Track enrollment changes for intrusion profiles
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Date Range Picker */}
                  <Box
                    onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 1,
                      px: 1.2,
                      py: 0.85,
                      cursor: 'pointer',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <CalendarMonth sx={{ color: 'white', fontSize: 18 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
                        ? format(dateRange[0].startDate, 'MMM dd, yyyy')
                        : `${format(dateRange[0].startDate, 'MMM dd')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={fetchAnalytics}
                    disabled={loading}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontWeight: 600,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      textTransform: 'uppercase',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Date Range Picker Popover */}
        <Popover
          open={datePickerOpen}
          anchorEl={datePickerAnchor}
          onClose={() => setDatePickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: { maxWidth: '100vw', overflow: 'auto' }
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Quick Select Presets */}
            <Box sx={{
              p: 2,
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
              minWidth: 140,
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Quick Select
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {[
                  { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                  { label: 'Last 14 Days', getValue: () => ({ startDate: addDays(new Date(), -13), endDate: new Date() }) },
                  { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                  { label: 'This Month', getValue: () => {
                    const now = new Date();
                    return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                  }},
                ].map((preset) => (
                  <Button
                    key={preset.label}
                    size="small"
                    variant="text"
                    onClick={() => {
                      const { startDate, endDate } = preset.getValue();
                      setDateRange([{ startDate, endDate, key: 'selection' }]);
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      px: 1.5,
                      py: 0.75,
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Date Range Picker */}
            <Box>
              <DateRangePicker
                ranges={dateRange}
                onChange={(ranges) => setDateRange([ranges.selection])}
                moveRangeOnFirstSelection={false}
                months={1}
                direction="horizontal"
                rangeColors={['#dc2626']}
                staticRanges={[]}
                inputRanges={[]}
                maxDate={new Date()}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                <Button onClick={() => setDatePickerAnchor(null)} variant="outlined" size="small">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setDatePickerAnchor(null);
                    fetchAnalytics();
                  }}
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>

        {/* Summary Information */}
        <Grow in timeout={1000}>
          <Box sx={{ mb: { xs: 2, sm: 2, md: 1 } }}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                background: 'white',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Net Registrations
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: netRegistrations >= 0 ? GREEN_COLOR : RED_COLOR }}>
                    {netRegistrations}
                  </Typography>
                </Box>
                {dateRangeLabel && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Date Range: {dateRangeLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Grow>

        {/* Bar Chart */}
        <Grow in timeout={1400}>
          <Box sx={{ mb: { xs: 2, sm: 3, md: 1 } }}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                background: 'white',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#40518A' }}>
                Daily Enrollment Changes
              </Typography>
              
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {error && !loading && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}

              {!loading && !error && (
                <>
                  {chartData.length > 0 ? (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                          <ReferenceLine y={0} stroke="#ccc" strokeWidth={1} />
                          <XAxis
                            dataKey="displayDate"
                            tick={{ fontSize: 11, fill: '#666' }}
                            axisLine={{ stroke: '#ccc' }}
                            angle={-35}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: '#666' }}
                            axisLine={{ stroke: '#ccc' }}
                            tickCount={9}
                            domain={yAxisDomain}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(220, 38, 38, 0.1)' }}
                            content={({ active, payload }) => {
                              if (!active || !payload || !payload.length) return null;
                              const item = payload[0].payload;
                              return (
                                <Box sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  padding: '8px',
                                  fontSize: '13px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                                    Date: {item.date}
                                  </Typography>
                                  <Typography variant="body2">
                                    Net Change: <span style={{ color: item.count >= 0 ? GREEN_COLOR : RED_COLOR, fontWeight: 600 }}>{item.count}</span>
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                                    Enrollments: {item.enrollments || 0}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                    Deletions: {item.deletions || 0}
                                  </Typography>
                                </Box>
                              );
                            }}
                          />
                          <Bar
                            dataKey="count"
                            name="Net Change"
                            radius={[4, 4, 0, 0]}
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.count >= 0 ? GREEN_COLOR : RED_COLOR}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 400,
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        bgcolor: '#f5f5f5'
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No enrollment data available for the selected date range
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Box>
        </Grow>
      </Container>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        minHeight: '100vh',
      }}
    >
      {renderIntrusionEnrollment()}
    </Box>
  );
};

export default IntrusionProfileEnrollment;
